import { useEffect, useState, useRef } from 'react';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { supabase } from '@readsavor/supabase-client';
import { useAuthStore } from '../store/auth.store';
/**
 * @name useRealtimeChannel
 * @description A robust React hook for managing a Supabase Realtime channel subscription.
 *
 * @param {string | null} channelName - The name of the channel to subscribe to. If null or undefined, the hook will do nothing.
 *
 * @returns {RealtimeChannel | null} - The Supabase RealtimeChannel instance once the subscription is active and stable, otherwise null.
 *
 * @purpose
 * This hook encapsulates the complex logic of subscribing to a Supabase channel, ensuring that the subscription
 * is only attempted when the user is authenticated and the global realtime connection
 * is ready. This is determined by the `isRealtimeReady` flag from `useAuthStore`.
 *
 * @how-it-works
 * 1. It tracks `session` and `isRealtimeReady` from `useAuthStore`.
 * 2. The main `useEffect` triggers whenever these values, or `channelName`, change.
 * 3. A channel subscription is only attempted if `session`, `channelName`, and `isRealtimeReady` are all truthy.
 * 4. If any of these conditions are not met (e.g., user logs out, connection drops), the `useEffect`'s
 *    cleanup function is triggered, ensuring the channel is properly removed.
 * 5. The consumer of this hook receives the channel instance only after a successful 'SUBSCRIBED' event,
 *    at which point it can confidently attach its own `.on()` listeners.
 */
export const useRealtimeChannel = (channelName) => {
    const { session, isRealtimeReady } = useAuthStore();
    const [channel, setChannel] = useState(null);
    const channelRef = useRef(null);
    useEffect(() => {
        // We can only subscribe if all conditions are met:
        // 1. User is logged in (session exists).
        // 2. A channel name is provided.
        // 3. The global realtime client is connected and ready.
        if (!session || !channelName || !isRealtimeReady) {
            // If conditions are not met, ensure we clean up any existing channel.
            // This handles logout, connection drops, or the component unmounting.
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
                setChannel(null);
            }
            return;
        }
        // If we already have a channel with the correct topic, do nothing.
        if (channelRef.current?.topic === `realtime:${channelName}`) {
            return;
        }
        // Create and subscribe to the new channel.
        const newChannel = supabase.channel(channelName);
        channelRef.current = newChannel;
        newChannel.subscribe((status, err) => {
            if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
                setChannel(newChannel);
            }
            if (err) {
                console.error(`[useRealtimeChannel] Subscription error for ${channelName}`, err);
                setChannel(null); // Ensure consumer knows the channel is not usable.
            }
        });
        // The main cleanup function for when the component unmounts.
        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
                setChannel(null);
            }
        };
    }, [session, channelName, isRealtimeReady]);
    return channel;
};
//# sourceMappingURL=useRealtimeChannel.js.map